import {
  AutocompleteArrayInput,
  BooleanInput,
  DateTimeInput,
  Link,
  NumberInput,
  ReferenceArrayInput,
  maxValue,
  minValue,
  required,
  useGetIdentity,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
  useTranslate as userReactAdminTranslate,
} from 'react-admin';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  GenderType,
  User,
  UserRole,
  User as UserType,
  Vehicle,
} from '../../@generated/schemas';
import { ReactNode, useEffect } from 'react';
import { omit, set, uniqBy } from 'lodash';
import {
  useGenerateUserOnboardingStripeLinkMutation,
  useResendEmailVerificationLinkMutation,
} from '../../@generated/hooks';

import { AssignedSharedVehicleGroupList } from '../sharedVehicleGroup/assignedList';
import CheckIcon from '@mui/icons-material/Check';
import CompanyInput from '../../common/CompanyInput';
import DateInput from '../../common/DateInput';
import { LoadingElement } from '../../App';
import SelectInput from '../../common/SelectInput';
import { TABS } from './tabs';
import { TabPanel } from '@mui/lab';
import TextInput from '../../common/TextInput';
import { convertEnumFilterItems } from '../../libs/convertEnumFilterItems';
import cookie from 'js-cookie';
import countriesList from '../../libs/countries.json';
import dayjs from 'dayjs';
import getStoragePath from '../../libs/getStoragePath';
import styled from 'styled-components';
import { url } from 'src';
import { useFormContext } from 'react-hook-form';
import { useTranslate } from '../../locales';

const StyledBorder = styled.div`
  border: 1px solid #e0e0e3;
  padding: 12px;
  padding-top: 20px;
  position: relative;
  border-radius: 4px;
`;

const StyledTypography = styled(Typography)`
  position: absolute;
  top: -16px;
  background: #fff;
  padding: 0 10px;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const LevelLabel = styled.div`
  border-radius: 11px;
  width: 100%;
  margin-right: 12px;
  margin-bottom: 8px;
  padding: 12px 12px;
`;

const LevelLabelItem = ({
  title,
  confirmed = false,
  additionalInfo,
}: {
  title: string;
  confirmed?: boolean | null;
  additionalInfo?: ReactNode | string | null;
}) => {
  return (
    <LevelLabel>
      <FlexRow>
        <div>{title}</div>
        {confirmed && <CheckIcon style={{ marginLeft: 12 }} width={14} />}
      </FlexRow>
      {!!confirmed ? (
        <i style={{ fontSize: 12 }}>
          {typeof confirmed !== 'boolean'
            ? `(${dayjs(confirmed).format('DD.MM. YYYY HH:mm')})`
            : ''}
        </i>
      ) : null}
      {additionalInfo && (
        <>
          <br />
          <i>{additionalInfo}</i>
        </>
      )}
    </LevelLabel>
  );
};

export const ManyToManySelectInput = (props) => {
  const { input, source, label } = props;

  const renderLabel = (choice) => {
    const value = choice || input.value;
    switch (source) {
      case 'vehicles_ids':
        return `${value?.vehicle?.vin || ''} (${
          value?.vehicle?.name ? `${value?.vehicle?.name}, ` : ''
        }${value?.vehicle?.plateNumber}, ${
          value?.vehicle?.brandType?.name || ''
        }, ${value?.vehicle?.modelType?.name || ''})`;
      case 'users':
        return `${value?.firstName || ''} ${value?.lastName || ''} (${
          value?.email
        })`;
      case 'sharedVehicleGroups':
        return value?.name || 'Clear';
    }
  };

  return (
    <FormControl sx={{ width: '50%' }}>
      <InputLabel>{label}</InputLabel>
      <ReferenceArrayInput
        source={props.source}
        label={label}
        reference="SharedVehicleGroupRelVehicle">
        <AutocompleteArrayInput optionText={renderLabel} />
      </ReferenceArrayInput>
    </FormControl>
  );
};

const DownloadPxlVision = ({ transactionCode, title }) => {
  return (
    (transactionCode && (
      <a
        target="_blank"
        href={`${url}/admin/pxl-vision-verification/${transactionCode}?access_token=${cookie.get(
          'access_token',
        )}`}
        download>
        {title}
      </a>
    )) ||
    null
  );
};

export const UserForm = (props) => {
  const { errors = {} } = props;
  const refresh = useRefresh();
  const notify = useNotify();
  const translate = useTranslate();
  const translateReactAdmin = userReactAdminTranslate();
  const { setValue, setError } = useFormContext();
  const record = useRecordContext<UserType>();
  const { identity } = useGetIdentity();

  useEffect(() => {
    Object.keys(errors).forEach((propertyName) => {
      console.log(propertyName, errors[propertyName]);
      setError(propertyName, { type: 'custom', message: errors[propertyName] });
    });
  }, [errors]);

  const [update] = useUpdate();

  const [stripeOnboarding, { loading }] =
    useGenerateUserOnboardingStripeLinkMutation();
  const [resendEmailVerificationLink] =
    useResendEmailVerificationLinkMutation();

  const handleUpdateProperty = async (property: string, value: any) => {
    let data = {
      [property]: value,
    };

    if (property === 'pxlVisionIdAt') {
      data = set(data, 'pxlVisionIdManuallyVerifiedBy', identity.id as string);
    }
    if (property === 'pxlVisionDriverLicenseAt') {
      data = set(
        data,
        'pxlVisionDriverLicenseManuallyVerifiedBy',
        identity.id as string,
      );
    }

    await update('User', { id: record?.id, data }, { returnPromise: true });
    refresh();
  };

  const handleResetStripeLink = async () => {
    try {
      await stripeOnboarding({
        variables: { userId: record.id as string },
      });

      notify(translate('admin.emailWasSuccessfullySent'), { type: 'success' });

      refresh();
    } catch (error) {
      notify(error.message, { type: 'error' });
    }
  };

  const handleResendEmailVerificationLink = async () => {
    try {
      await resendEmailVerificationLink({
        variables: { userId: record.id as string },
      });

      notify(translate('admin.emailWasSuccessfullySent'), { type: 'success' });

      refresh();
    } catch (error) {
      notify(error.message, { type: 'error' });
    }
  };

  let _UserRole = UserRole;
  if (identity?.userRole === UserRole.CompanyAdmin) {
    _UserRole = omit(_UserRole, 'SuperAdmin') as any;
  }

  return (
    <TabPanel value={TABS.basicInfo}>
      <Grid container spacing={8}>
        <Grid item md={4}>
          <StyledBorder>
            <StyledTypography variant="h6" style={{ marginBottom: 24 }}>
              {translate('admin.basicInfo')}
            </StyledTypography>
            <TextInput
              source="firstName"
              label={translate('admin.firstName')}
              error={errors?.firstName}
              style={{ width: '100%' }}
            />
            <TextInput
              source="middleName"
              label={translate('admin.middleName')}
              error={errors?.middleName}
              style={{ width: '100%' }}
            />
            <TextInput
              source="lastName"
              label={translate('admin.lastName')}
              error={errors?.lastName}
              style={{ width: '100%' }}
            />
            <TextInput
              source="email"
              label={translate('admin.emailField')}
              validate={[required()]}
              error={errors?.email}
              style={{ width: '100%' }}
            />
            {identity?.userRole === UserRole.SuperAdmin && (
              <CompanyInput
                value={record?.companyId || undefined}
                source="companyId"
                onChange={(event) => setValue('company', event.target.value)}
              />
            )}
            {!record?.id && (
              <TextInput
                source="password"
                label={translate('admin.password')}
                error={errors?.password}
                validate={[required()]}
                style={{ width: '100%' }}
              />
            )}

            <TextInput
              source="phone"
              label={translate('admin.phone')}
              error={errors?.phone}
              validate={[required()]}
              style={{ width: '100%' }}
            />
            <SelectInput
              source="language"
              label={translate('admin.language')}
              error={errors?.language}
              choices={['DE', 'FR', 'IT', 'EN'].map((language) => ({
                id: language,
                name: language,
              }))}
              style={{ width: '100%' }}
            />
            {[UserRole.SuperAdmin, UserRole.CompanyAdmin].includes(
              identity?.userRole,
            ) && (
              <SelectInput
                source="userRole"
                label={translate('admin.userRole')}
                fullWidth
                choices={convertEnumFilterItems(_UserRole)}
              />
            )}

            {record?.id && (
              <FlexRow style={{ marginTop: 12, flexWrap: 'wrap' }}>
                <FlexRow style={{ width: '100%' }}>
                  <LevelLabelItem
                    title={translate('admin.emailVerification')}
                    confirmed={record?.userLevel?.steps?.emailConfirmedAt}
                  />
                  <Button
                    variant="contained"
                    style={{ marginRight: 6 }}
                    onClick={handleResendEmailVerificationLink}>
                    {translate('admin.resend')}
                  </Button>
                  <div>
                    <Button
                      variant="contained"
                      style={{ marginBottom: 6 }}
                      onClick={() =>
                        handleUpdateProperty('emailConfirmedAt', {
                          set: null,
                        })
                      }>
                      {translate('admin.reset')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        handleUpdateProperty(
                          'emailConfirmedAt',
                          dayjs().toDate(),
                        )
                      }>
                      {translate('admin.verify')}
                    </Button>
                  </div>
                </FlexRow>
                <FlexRow style={{ width: '100%' }}>
                  <LevelLabelItem
                    title={translate('admin.phoneVerification')}
                    confirmed={record?.userLevel?.steps?.phoneConfirmedAt}
                  />
                  <Button
                    variant="contained"
                    style={{ marginRight: 6 }}
                    onClick={() =>
                      handleUpdateProperty('phoneConfirmedAt', {
                        set: null,
                      })
                    }>
                    {translate('admin.reset')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleUpdateProperty('phoneConfirmedAt', dayjs().toDate())
                    }>
                    {translate('admin.verify')}
                  </Button>
                </FlexRow>
                <FlexRow style={{ width: '100%' }}>
                  <LevelLabelItem
                    title={translate('admin.idVerification')}
                    confirmed={record?.pxlVisionIdAt}
                    additionalInfo={
                      record?.pxlVisionIdAt &&
                      record?.pxlVisionIdManuallyVerifiedBy?.id && (
                        <div style={{ fontSize: 12 }}>
                          <u>
                            {translate('admin.verifiedBy')}:{' '}
                            {record?.pxlVisionIdManuallyVerifiedBy?.email}
                          </u>
                        </div>
                      )
                    }
                  />
                  <Button
                    variant="contained"
                    style={{ marginRight: 6 }}
                    onClick={() =>
                      handleUpdateProperty('pxlVisionIdAt', {
                        set: null,
                      })
                    }>
                    {translate('admin.reset')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleUpdateProperty('pxlVisionIdAt', dayjs().toDate())
                    }>
                    {translate('admin.verify')}
                  </Button>
                </FlexRow>
                <FlexRow style={{ width: '100%' }}>
                  <LevelLabelItem
                    title={translate('admin.driverLicenseVerification')}
                    confirmed={record?.pxlVisionDriverLicenseAt}
                    additionalInfo={
                      record?.pxlVisionDriverLicenseAt &&
                      record?.pxlVisionDriverLicenseManuallyVerifiedBy?.id && (
                        <div style={{ fontSize: 12 }}>
                          <u>
                            {translate('admin.verifiedBy')}:{' '}
                            {
                              record?.pxlVisionDriverLicenseManuallyVerifiedBy
                                ?.email
                            }
                          </u>
                        </div>
                      )
                    }
                  />
                  <Button
                    variant="contained"
                    style={{ marginRight: 6 }}
                    onClick={() =>
                      handleUpdateProperty('pxlVisionDriverLicenseAt', {
                        set: null,
                      })
                    }>
                    {translate('admin.reset')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleUpdateProperty(
                        'pxlVisionDriverLicenseAt',
                        dayjs().toDate(),
                      )
                    }>
                    {translate('admin.verify')}
                  </Button>
                </FlexRow>
                <LevelLabelItem
                  title={translate('admin.paymentMethodVerification')}
                  confirmed={record?.userLevel?.steps?.paymentMethod}
                  additionalInfo={
                    <div style={{ fontSize: 12 }}>
                      Twint:{' '}
                      {!!record.stripeTwintConfimedAt
                        ? dayjs(record.stripeTwintConfimedAt).format(
                            'DD.MM. YYYY HH:mm',
                          )
                        : '-'}
                      <br />
                      {translate('admin.cards')}:{' '}
                      {record?.paymentCards
                        ?.map((paymentCard) => `...${paymentCard.last4}`)
                        ?.join(', ')}
                    </div>
                  }
                />
                <LevelLabelItem
                  title={`${translate('admin.userLevel')}: ${
                    record?.userLevel?.level < 100
                      ? record?.userLevel?.level === 0
                        ? 0
                        : 1
                      : 2
                  }`}
                />
              </FlexRow>
            )}

            {record?.stripeAccountLink && (
              <>
                <a target="_blank" href={record?.stripeAccountLink}>
                  {translate('admin.stripeAccountOnboarding')}
                </a>
                <br />
                <br />
              </>
            )}

            <DateTimeInput
              label={translate('admin.stripeAccountConnectedAt')}
              variant="outlined"
              size="small"
              style={{ width: '100%' }}
              source="stripeAccountConfirmedAt"
            />

            {record?.stripeAccountId ? (
              <>
                <Link
                  disabled={loading}
                  target="_blank"
                  to={`https://dashboard.stripe.com/connect/accounts/${record?.stripeAccountId}`}>
                  <Button
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size="1em" />}>
                    {translate('admin.openStripeDetail')}
                  </Button>
                </Link>
                <br />
                <br />
              </>
            ) : null}
            <Button
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size="1em" />}
              onClick={handleResetStripeLink}>
              {translate('admin.resendStripeLink')}
            </Button>
          </StyledBorder>
        </Grid>
        <Grid item md={4}>
          <StyledBorder>
            <StyledTypography variant="h6" style={{ marginBottom: 24 }}>
              {translate('admin.addressInfo')}
            </StyledTypography>

            <SelectInput
              source="gender"
              label={translate('admin.gender')}
              variant="outlined"
              error={errors?.gender}
              choices={[GenderType.Male, GenderType.Female].map((language) => ({
                id: language,
                name: translate(language.toLowerCase()),
              }))}
              style={{ width: '100%' }}
            />
            <DateInput
              source="birthdate"
              label={translate('admin.birthdate')}
              error={errors?.birthdate}
              style={{ width: '100%' }}
            />
            <SelectInput
              label={translate('admin.nationality')}
              source="nationality"
              error={errors?.nationality}
              variant="outlined"
              size="small"
              choices={countriesList.map((country) => ({
                name: country.name,
                id: country.alpha2,
              }))}
              style={{ width: '100%' }}
            />
            {!['ch'].includes(record?.nationality || '') && (
              <SelectInput
                source="residencePermit"
                label={translate('admin.residencePermit')}
                error={errors?.residencePermit}
                variant="outlined"
                size="small"
                translateChoice={false}
                choices={[
                  {
                    name: translate('admin.settlementPermit'),
                    id: '1',
                  },
                  {
                    name: translate('admin.seasonPermit'),
                    id: '2',
                  },
                  {
                    name: translate('admin.annualPermit'),
                    id: '3',
                  },
                  {
                    name: translate('admin.asylumSeekers'),
                    id: '4',
                  },
                  {
                    name: translate('admin.specialPermit'),
                    id: '5',
                  },
                  {
                    name: translate('admin.specialRegulation'),
                    id: '6',
                  },
                  {
                    name: translate('admin.diplomats'),
                    id: '7',
                  },
                  {
                    name: translate('admin.crossBorderCommuterPermit'),
                    id: '8',
                  },
                  {
                    name: translate('admin.shortStayPermit'),
                    id: '9',
                  },
                  {
                    name: translate('admin.preliminaryRecording'),
                    id: '10',
                  },
                  {
                    name: translate('admin.noPermit'),
                    id: '11',
                  },
                ]}
                style={{ width: '100%' }}
              />
            )}
            <TextInput
              source="address"
              label={translate('admin.address')}
              error={errors?.address}
              style={{ width: '100%' }}
            />
            <TextInput
              source="houseNumber"
              label={translate('admin.houseNumber')}
              error={errors?.houseNumber}
              style={{ width: '100%' }}
            />
            <TextInput
              source="city"
              label={translate('admin.city')}
              error={errors?.city}
              style={{ width: '100%' }}
            />
            <TextInput
              source="postCode"
              label={translate('admin.postCode')}
              error={errors?.postCode}
              style={{ width: '100%' }}
            />

            {record?.pxlVisionIdCode && (
              <DownloadPxlVision
                transactionCode={record.pxlVisionIdCode}
                title={translate('admin.downloadIdPxlVision')}
              />
            )}
          </StyledBorder>

          <StyledBorder style={{ marginTop: 30 }}>
            <StyledTypography variant="h6" style={{ marginBottom: 24 }}>
              {translate('admin.commission')}
            </StyledTypography>

            <Row>
              <NumberInput
                label={translate('admin.commissionForBookings')}
                source="commissionBookings"
                validate={[minValue(0), maxValue(100)]}
              />
            </Row>
            <Row>
              <NumberInput
                label={translate('admin.commissionForChargingStations')}
                source="commissionChargingStations"
                validate={[minValue(0), maxValue(100)]}
              />
            </Row>
            <Row>
              <NumberInput
                label={translate('admin.commissionForScooters')}
                source="commissionScooters"
                validate={[minValue(0), maxValue(100)]}
              />
            </Row>
          </StyledBorder>
          <StyledBorder style={{ marginTop: 30 }}>
            <StyledTypography variant="h6" style={{ marginBottom: 24 }}>
              {translate('admin.notificationsField')}
            </StyledTypography>

            <Row>
              <BooleanInput
                source="notificationsIsEnabledSms"
                label={translate('admin.smsNotification')}
              />
            </Row>
            <Row>
              <BooleanInput
                source="notificationsIsEnabledEmail"
                label={translate('admin.emailField')}
              />
            </Row>
            <Row>
              <BooleanInput
                label={translate('admin.pushNotifications')}
                source="notificationsIsEnabledPush"
              />
            </Row>
          </StyledBorder>
        </Grid>
        <Grid item md={4}>
          <StyledBorder>
            <StyledTypography variant="h6" style={{ marginBottom: 24 }}>
              {translate('admin.driverLicence')}
            </StyledTypography>

            <TextInput
              source="driverFirstName"
              label={translate('admin.driverFirstName')}
              error={errors?.driverFirstName}
              style={{ width: '100%' }}
            />
            <TextInput
              source="driverMiddleName"
              label={translate('admin.driverMiddleName')}
              error={errors?.driverMiddleName}
              style={{ width: '100%' }}
            />
            <TextInput
              source="driverLastName"
              label={translate('admin.driverLastName')}
              error={errors?.driverLastName}
              style={{ width: '100%' }}
            />
            <TextInput
              source="driverLicenceNumber"
              label={translate('admin.driverLicenceNumber')}
              error={errors?.driverLicenceNumber}
              style={{ width: '100%' }}
            />
            <DateInput
              source="driverExpirationAt"
              label={translate('admin.driverExpirationAt')}
              error={errors?.driverExpirationAt}
              style={{ width: '100%' }}
            />
            <DateInput
              source="driverDateOfIssueAt"
              label={translate('admin.driverDateOfIssueAt')}
              error={errors?.driverDateOfIssueAt}
              style={{ width: '100%' }}
            />
            <DateInput
              source="driverBirthdate"
              label={translate('admin.driverBirthdate')}
              error={errors?.driverBirthdate}
              style={{ width: '100%' }}
            />
            {record?.pxlVisionDriverLicenseCode && (
              <DownloadPxlVision
                transactionCode={record.pxlVisionDriverLicenseCode}
                title={translate('admin.downloadDriverLicensePxlVision')}
              />
            )}
          </StyledBorder>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export const UserAside = () => {
  const record = useRecordContext<User>();
  const translate = useTranslate();
  const { data, isLoading } = useGetList('Vehicle', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'createdAt', order: 'DESC' },
    filter: {
      userId: record?.id || 'created',
    },
  });

  return (
    <Card
      variant="outlined"
      style={{
        minWidth: 300,
        margin: '0 1em 0',
        float: 'right',
        height: '90vh',
        position: 'sticky',
        top: 30,
      }}>
      <CardContent style={{ height: '50vh' }}>
        <Typography>{translate('admin.assignedVehicles')}</Typography>

        {(isLoading && <LoadingElement />) || (
          <List
            sx={{
              overflow: 'auto',
              height: '35vh',
              maxWidth: 360,
              bgcolor: 'background.paper',
            }}>
            {Object.values(data).map((vehicle: Vehicle) => (
              <ListItemButton
                key={vehicle.id}
                alignItems="flex-start"
                href={`#/Vehicle/${vehicle.id}`}
                target="_blank"
                component="a">
                <ListItemAvatar>
                  <Avatar
                    alt="Vehicle image"
                    src={getStoragePath(
                      `gallery/${vehicle?.defaultVehicleImage}`,
                    )}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={`${vehicle.name ? `${vehicle.name}, ` : ''}${
                    vehicle.vin
                  }`}
                  secondary={
                    vehicle.disabled
                      ? translate('admin.unpublished')
                      : translate('admin.published')
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </CardContent>
      <CardContent style={{ height: '50vh' }}>
        <Typography>{translate('admin.assignedSvgs')}</Typography>

        {(isLoading && <LoadingElement />) || (
          <AssignedSharedVehicleGroupList
            sharedVehicleGroupRels={uniqBy(
              [
                ...(record?.sharedVehicleGroupRelUser || []),
                ...(record?.company?.sharedVehicleGroupRelCompany || []),
                ...(record?.company?.defaultSharedVehicleGroup
                  ? [
                      {
                        id: '',
                        sharedVehicleGroup:
                          record?.company?.defaultSharedVehicleGroup,
                      },
                    ]
                  : []),
              ],
              'sharedVehicleGroup.name',
            ).filter((item) => item.sharedVehicleGroup.deletedAt === null)}
          />
        )}
      </CardContent>
    </Card>
  );
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  & > div {
    flex: 1;
  }
  div ~ div {
    margin-left: 10px;
  }
`;
